<template>
  <div v-if="isVisible" class="edit-overlay">
    <div class="edit-overlay-backdrop" @click="close"></div>
    <div class="edit-overlay-content">
      <div class="edit-header">
        <h2>Remove MPID</h2>
        <button class="close-button" @click="close">
          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16"
               viewBox="0 0 24 24" fill="none" stroke="currentColor"
               stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <line x1="18" y1="6" x2="6" y2="18"></line>
            <line x1="6" y1="6" x2="18" y2="18"></line>
          </svg>
        </button>
      </div>

      <div class="form-container">
        <div v-if="loading" class="loading-overlay">
          <div class="loading-spinner"></div>
          <span>Loading...</span>
        </div>
        <div v-else class="form-content">
          <div class="form-section">
            <p class="confirmation-message">
              Are you sure you want to detach MPID <strong>{{ currentMpid }}</strong> from this device?
            </p>
            <div v-if="error" class="error-message">{{ error }}</div>
          </div>
        </div>
      </div>

      <div class="edit-actions">
        <button class="action-button" @click="close" :disabled="loading">Cancel</button>
        <button class="action-button submit-button" @click="removeMpid" :disabled="loading">
          Confirm
        </button>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'DeviceDetailsRemoveMpidOverlay',
  props: {
    isVisible: {
      type: Boolean,
      default: false
    },
    deviceId: {
      type: String,
      required: true
    },
    currentMpid: {
      type: String,
      required: true
    }
  },
  data() {
    return {
      loading: false,
      error: ''
    };
  },
  methods: {
    async removeMpid() {
      this.loading = true;
      this.error = '';
      const apiBaseUrl = process.env.VUE_APP_API_URL || 'http://localhost:8000/api';
      const url = `${apiBaseUrl}/devices/${this.deviceId}/mpid/remove`;
      try {
        const response = await fetch(url, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('access_token')}`
          }
        });
        if (response.ok) {
          const updatedDevice = await response.json();
          this.$emit('mpid-removed', updatedDevice);
          this.close();
        } else {
          const resData = await response.json();
          this.error = resData.message || 'An error occurred while removing the MPID';
        }
      } catch (err) {
        console.error('Error removing MPID:', err);
        this.error = 'Failed to remove MPID. Please try again.';
      } finally {
        this.loading = false;
      }
    },
    close() {
      this.$emit('close');
    }
  }
};
</script>

<style scoped>
/* Overlay */
.edit-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.edit-overlay-backdrop {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: linear-gradient(to top, rgba(32,32,32,0.1) 0%, rgba(16,16,16,0.95) 100%);
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(4px);
  animation: fadeIn 0.3s ease forwards;
}

.edit-overlay-content {
  position: relative;
  width: 700px;
  max-width: 90%;
  max-height: 90vh;
  border-radius: 8px;
  padding: 24px;
  color: white;
  background-color: rgba(50,50,50,0.2);
  backdrop-filter: blur(16px);
  box-shadow: 0 6px 16px rgba(0,0,0,0.3);
  overflow-y: auto;
  animation: slideIn 0.3s ease forwards;
  z-index: 1001;
}

/* Header */
.edit-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
  border-bottom: 1px solid rgba(255,255,255,0.1);
  padding-bottom: 16px;
}

.edit-header h2 {
  font-size: 18px;
  font-weight: 700;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  margin: 0;
}

.close-button {
  background: transparent;
  border: none;
  color: rgba(255,255,255,0.6);
  cursor: pointer;
  padding: 4px;
  transition: color 0.2s;
}

.close-button:hover {
  color: rgba(255,255,255,0.9);
}

/* Form container */
.form-container {
  position: relative;
  min-height: 200px;
}

.loading-overlay {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  z-index: 10;
  background-color: rgba(0,0,0,0.6);
  border-radius: 8px;
  gap: 16px;
}

.loading-spinner {
  width: 30px;
  height: 30px;
  border: 3px solid rgba(255,255,255,0.1);
  border-top: 3px solid rgba(255,255,255,0.8);
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.error-message {
  color: #ff8a80;
  text-align: center;
  margin-top: 8px;
  font-size: 12px;
}

/* Confirmation message */
.confirmation-message {
  font-size: 14px;
  text-align: center;
  margin: 20px 0;
}

/* Action buttons */
.edit-actions {
  display: flex;
  justify-content: flex-end;
  gap: 16px;
  margin-top: 24px;
  padding-top: 20px;
  border-top: 1px solid rgba(255,255,255,0.1);
}

.action-button {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  background: none;
  border: none;
  color: white;
  font-size: 11px;
  font-weight: 500;
  cursor: pointer;
  padding: 8px 16px;
  border-radius: 4px;
  transition: background-color 0.2s;
}

.action-button:hover:not(:disabled) {
  background-color: rgba(255,255,255,0.1);
}

.action-button:disabled {
  opacity: 0.3;
  cursor: not-allowed;
}

.submit-button {
  background-color: rgba(79,195,247,0.2);
}

.submit-button:hover:not(:disabled) {
  background-color: rgba(79,195,247,0.3);
}

@keyframes fadeIn {
  from { opacity: 0; }
  to { opacity: 1; }
}

@keyframes slideIn {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}
</style>
